import {
  formatarPlaca,
  ROTULO_DO_STATUS_DE_VEICULO,
  ROTULO_DO_TIPO_DE_LOCAL,
  STATUS_DE_VEICULO,
  type StatusDoVeiculo,
} from '@locaveri/shared'
import { Link } from '@tanstack/react-router'
import type { ColumnDef } from '@tanstack/react-table'
import {
  ArrowRightLeft,
  Car,
  CircleArrowDown,
  EllipsisVertical,
  ExternalLink,
  LayoutGrid,
  LoaderCircle,
  Pencil,
  Plus,
  Rows3,
  Search,
} from 'lucide-react'
import { type ReactElement, useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { z } from 'zod'

import { Paginacao } from '@/components/data/paginacao'
import { TabelaDeDados } from '@/components/data/tabela-de-dados'
import { EmptyState } from '@/components/state/empty-state'
import { ErrorState } from '@/components/state/error-state'
import { LoadingState } from '@/components/state/loading-state'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import {
  gravidadeDaData,
  identificacaoDoVeiculo,
  useBaixarVeiculo,
  useCategoriasAtivas,
  useLocaisAtivos,
  useVeiculos,
  type Veiculo,
} from '@/features/frota/api'
import {
  BadgeDeGravidadeDeAlerta,
  BadgeDeNivelDeBlindagem,
  BadgeDeStatusDoVeiculo,
} from '@/features/frota/badges'
import { DialogoDeStatus } from '@/features/frota/dialogo-de-status'
import { DialogoDeVeiculo } from '@/features/frota/dialogo-de-veiculo'
import { errorMessage } from '@/lib/api'
import { formatarData, SEM_VALOR } from '@/lib/datas'
import { useValorAtrasado } from '@/lib/hooks'
import { usePermissao } from '@/lib/permissoes'
import { cn } from '@/lib/utils'

/**
 * A frota: os carros de verdade, com placa.
 *
 * ---------------------------------------------------------------------------
 * DUAS VISÕES PORQUE SÃO DUAS PERGUNTAS
 * ---------------------------------------------------------------------------
 * A TABELA responde "o que está fora de circulação e desde quando" — compara
 * linhas, ordena, cabe muita coisa por tela. Os CARDS respondem "como está este
 * carro" — é a leitura do balcão, no celular, com o cliente esperando. Nenhuma
 * das duas substitui a outra, e por isso a escolha fica gravada: quem trabalha
 * de um jeito não quer reescolher a cada visita.
 *
 * A preferência (visão + filtros) vive em `localStorage`, e não no servidor,
 * porque é preferência de PESSOA e de MÁQUINA — o gestor no desktop filtra por
 * vencimento de blindagem, o balcão filtra por local. Toda leitura passa por
 * try/catch: navegador com armazenamento bloqueado LANÇA no `getItem`, e uma
 * preferência de tela não pode derrubar a lista da frota.
 *
 * ---------------------------------------------------------------------------
 * A PLACA APARECE FORMATADA E É BUSCADA DE QUALQUER JEITO
 * ---------------------------------------------------------------------------
 * No banco ela é canônica (`ABC1D23`); na tela sai com hífen, que é como a
 * operação lê e dita no telefone. A busca vai crua para o servidor, que
 * normaliza — quem digita "abc-1d23" acha o mesmo carro que quem digita
 * "ABC1D23".
 *
 * ---------------------------------------------------------------------------
 * ONDE ESTÁ O AMARELO
 * ---------------------------------------------------------------------------
 * Num lugar só: "Novo veículo". Status é semântico (verde/âmbar/vermelho) e
 * nunca de marca; nível de blindagem é contorno, porque aparece em toda linha e
 * pintaria a tela inteira.
 */

const TAMANHO_DA_PAGINA = 20

/** Chave da preferência local. O prefixo evita colisão com outra tela. */
const CHAVE_DAS_PREFERENCIAS = 'locaveri:frota:preferencias'

const VISOES = ['tabela', 'cards'] as const

type Visao = (typeof VISOES)[number]

/** Janelas oferecidas no filtro de blindagem a vencer, em dias. */
const JANELAS_DE_BLINDAGEM = [15, 30, 60, 90] as const

type JanelaDeBlindagem = (typeof JANELAS_DE_BLINDAGEM)[number]

/**
 * O que fica gravado por pessoa.
 *
 * É validado com Zod na leitura porque `localStorage` é texto que qualquer
 * versão anterior (ou qualquer extensão) pode ter escrito: um `status` que não
 * existe mais viraria um filtro que a API recusa com 422, e a tela abriria em
 * erro sem que ninguém tivesse feito nada.
 */
const preferenciasSchema = z.object({
  visao: z.enum(VISOES),
  busca: z.string(),
  categoryId: z.string(),
  locationId: z.string(),
  status: z.union([z.enum(STATUS_DE_VEICULO), z.literal('')]),
  // Só as janelas que o seletor oferece. Um valor gravado por outra versão (45
  // dias, digamos) não casaria com nenhuma `<option>`: o campo voltaria a
  // mostrar "Qualquer prazo" e a lista continuaria filtrada por um prazo que
  // ninguém enxerga — o mesmo bug do id órfão de categoria, e tão silencioso
  // quanto.
  blindagemVencendoEmDias: z.union([z.literal(JANELAS_DE_BLINDAGEM), z.literal('')]),
  incluirBaixados: z.boolean(),
})

type Preferencias = z.infer<typeof preferenciasSchema>

const PREFERENCIAS_PADRAO: Preferencias = {
  visao: 'tabela',
  busca: '',
  categoryId: '',
  locationId: '',
  status: '',
  blindagemVencendoEmDias: '',
  incluirBaixados: false,
}

function lerPreferencias(): Preferencias {
  try {
    const bruto = window.localStorage.getItem(CHAVE_DAS_PREFERENCIAS)

    if (bruto === null) {
      return PREFERENCIAS_PADRAO
    }

    // Anotado como `unknown`: `JSON.parse` devolve `any`, e `any` atravessando a
    // fronteira é exatamente o que o `strict` do projeto proíbe.
    const dados: unknown = JSON.parse(bruto)
    const validado = preferenciasSchema.safeParse(dados)

    return validado.success ? validado.data : PREFERENCIAS_PADRAO
  } catch {
    // Aba anônima, política corporativa ou cota estourada: o acesso LANÇA.
    return PREFERENCIAS_PADRAO
  }
}

function gravarPreferencias(preferencias: Preferencias): void {
  try {
    window.localStorage.setItem(CHAVE_DAS_PREFERENCIAS, JSON.stringify(preferencias))
  } catch {
    // Preferência não gravada é um incômodo; erro na tela da frota é um chamado.
  }
}

/** Hodômetro é número, não dinheiro — mas continua exigindo largura fixa. */
const NUMERO_PT_BR = new Intl.NumberFormat('pt-BR')

function formatarKm(valor: number): string {
  return `${NUMERO_PT_BR.format(valor)} km`
}

export function FrotaPage(): ReactElement {
  const podeCriar = usePermissao('vehicle.create')
  const podeEditar = usePermissao('vehicle.update')
  const podeBaixar = usePermissao('vehicle.delete')

  // Uma leitura só, no primeiro render: reler a cada render devolveria objeto
  // novo e faria os estados iniciais brigarem com o efeito de gravação.
  const iniciais = useMemo(lerPreferencias, [])

  const [visao, setVisao] = useState<Visao>(iniciais.visao)
  const [pagina, setPagina] = useState(1)
  const [buscaDigitada, setBuscaDigitada] = useState(iniciais.busca)
  const [categoryId, setCategoryId] = useState(iniciais.categoryId)
  const [locationId, setLocationId] = useState(iniciais.locationId)
  const [status, setStatus] = useState<StatusDoVeiculo | ''>(iniciais.status)
  const [blindagem, setBlindagem] = useState<JanelaDeBlindagem | ''>(
    iniciais.blindagemVencendoEmDias,
  )
  const [incluirBaixados, setIncluirBaixados] = useState(iniciais.incluirBaixados)

  const [dialogoAberto, setDialogoAberto] = useState(false)
  const [emEdicao, setEmEdicao] = useState<Veiculo | undefined>(undefined)
  const [veiculoDoStatus, setVeiculoDoStatus] = useState<Veiculo | null>(null)
  const [veiculoDaBaixa, setVeiculoDaBaixa] = useState<Veiculo | null>(null)

  const busca = useValorAtrasado(buscaDigitada)

  // Grava o filtro JÁ ATRASADO: escrever a cada tecla encheria o armazenamento
  // de estados intermediários que ninguém pediu para guardar.
  useEffect(() => {
    gravarPreferencias({
      visao,
      busca,
      categoryId,
      locationId,
      status,
      blindagemVencendoEmDias: blindagem,
      incluirBaixados,
    })
  }, [visao, busca, categoryId, locationId, status, blindagem, incluirBaixados])

  const filtros = useMemo(
    () => ({
      pagina,
      tamanho: TAMANHO_DA_PAGINA,
      busca,
      categoryId,
      locationId,
      status,
      blindagemVencendoEmDias: blindagem,
      incluirBaixados,
    }),
    [pagina, busca, categoryId, locationId, status, blindagem, incluirBaixados],
  )

  const consulta = useVeiculos(filtros)
  const categorias = useCategoriasAtivas()
  const locais = useLocaisAtivos()

  // Filtro gravado apontando para uma categoria desativada (ou uma base
  // fechada) é a pior classe de bug de tela: o seletor volta a mostrar "Todas"
  // porque nenhuma opção casa com o valor, e a lista continua filtrada por um
  // id que ninguém enxerga. Quando as listas chegam, o id órfão cai.
  useEffect(() => {
    const itens = categorias.data?.itens

    if (itens !== undefined && categoryId !== '' && !itens.some((item) => item.id === categoryId)) {
      setCategoryId('')
    }
  }, [categorias.data, categoryId])

  useEffect(() => {
    const itens = locais.data?.itens

    if (itens !== undefined && locationId !== '' && !itens.some((item) => item.id === locationId)) {
      setLocationId('')
    }
  }, [locais.data, locationId])

  // A lista de veículos tem os três estados na `TabelaDeDados`; as listas de
  // apoio não tinham nenhum. Quando elas falham, os dois seletores ficam só com
  // "Todas" — indistinguível de uma operação sem nenhuma categoria cadastrada.
  // O aviso existe para que ninguém conclua que o filtro está vazio quando ele
  // está quebrado.
  const listasDeApoioComErro = categorias.isError || locais.isError

  const temFiltro =
    busca.length > 0 ||
    categoryId !== '' ||
    locationId !== '' ||
    status !== '' ||
    blindagem !== '' ||
    incluirBaixados

  function limparFiltros(): void {
    setBuscaDigitada('')
    setCategoryId('')
    setLocationId('')
    setStatus('')
    setBlindagem('')
    setIncluirBaixados(false)
    setPagina(1)
  }

  function abrirCriacao(): void {
    setEmEdicao(undefined)
    setDialogoAberto(true)
  }

  function abrirEdicao(veiculo: Veiculo): void {
    setEmEdicao(veiculo)
    setDialogoAberto(true)
  }

  const colunas = useMemo<ColumnDef<Veiculo>[]>(
    () => [
      {
        id: 'identificacao',
        header: 'Veículo',
        accessorFn: (linha: Veiculo) => `${linha.modelo} ${linha.placa}`,
        cell: ({ row }) => {
          const veiculo: Veiculo = row.original

          return (
            <div className="min-w-0">
              <Link
                to="/frota/$veiculoId"
                params={{ veiculoId: veiculo.id }}
                // `block`: `truncate` não faz nada num `<a>` inline, e sem ele
                // um modelo longo empurra a largura da tabela em 375px.
                className="block truncate font-medium text-foreground hover:underline"
              >
                {veiculo.modelo}
              </Link>
              <p className="truncate text-xs tabular-money text-fg-secondary">
                {formatarPlaca(veiculo.placa)} · {veiculo.marca} · {veiculo.anoFabricacao}/
                {veiculo.anoModelo}
              </p>
            </div>
          )
        },
      },
      {
        id: 'categoria',
        header: 'Categoria',
        accessorFn: (linha: Veiculo) => linha.categoria.nome,
        cell: ({ row }) => {
          const veiculo: Veiculo = row.original

          return (
            <div className="flex min-w-0 flex-col gap-1">
              <span className="truncate text-sm text-foreground">{veiculo.categoria.nome}</span>
              <BadgeDeNivelDeBlindagem nivel={veiculo.categoria.nivelDeBlindagem} />
            </div>
          )
        },
      },
      {
        id: 'status',
        header: 'Situação',
        enableSorting: false,
        cell: ({ row }) => {
          const veiculo: Veiculo = row.original

          return (
            <div className="flex flex-wrap items-center gap-1.5">
              <BadgeDeStatusDoVeiculo status={veiculo.statusAtual} />
              {veiculo.dataDeBaixa === null ? null : (
                <Badge variant="neutral" title="Fora da frota — o histórico continua disponível">
                  Baixado
                </Badge>
              )}
            </div>
          )
        },
      },
      {
        id: 'local',
        header: 'Base',
        accessorFn: (linha: Veiculo) => linha.local?.nome ?? '',
        cell: ({ row }) => {
          const local = row.original.local

          if (local === null) {
            return <span className="text-xs text-fg-muted">{SEM_VALOR}</span>
          }

          return (
            <div className="min-w-0">
              <p className="truncate text-sm text-foreground">{local.nome}</p>
              <p className="truncate text-xs text-fg-secondary">
                {ROTULO_DO_TIPO_DE_LOCAL[local.tipo]}
              </p>
            </div>
          )
        },
      },
      {
        id: 'hodometro',
        header: 'Hodômetro',
        meta: { alinhamento: 'direita' },
        accessorFn: (linha: Veiculo) => linha.hodometroAtual,
        cell: ({ row }) => (
          <span className="money-cell block text-sm text-fg-secondary">
            {formatarKm(row.original.hodometroAtual)}
          </span>
        ),
      },
      {
        id: 'blindagem',
        header: 'Blindagem até',
        accessorFn: (linha: Veiculo) => linha.certificadoDeBlindagemValidade ?? '',
        cell: ({ row }) => (
          <ValidadeDaBlindagem data={row.original.certificadoDeBlindagemValidade} />
        ),
      },
      {
        id: 'acoes',
        header: '',
        enableSorting: false,
        cell: ({ row }) => (
          <AcoesDoVeiculo
            veiculo={row.original}
            podeEditar={podeEditar}
            podeBaixar={podeBaixar}
            aoEditar={abrirEdicao}
            aoMudarStatus={setVeiculoDoStatus}
            aoBaixar={setVeiculoDaBaixa}
          />
        ),
      },
    ],
    // O que muda o conjunto de colunas é a permissão: as funções de ação são
    // estáveis dentro do render desta tela.
    [podeEditar, podeBaixar],
  )

  const dados = consulta.data
  const itens = dados?.itens ?? []

  const estadoVazio = (
    <EmptyState
      icone={Car}
      titulo={temFiltro ? 'Nenhum veículo com esses filtros' : 'Nenhum veículo cadastrado'}
      descricao={
        temFiltro
          ? 'Ajuste a busca, a categoria, a base ou a situação para ver outros resultados.'
          : 'Cadastre o primeiro carro da frota. A categoria define o que o cliente aluga; aqui fica o ativo físico.'
      }
      acao={
        temFiltro ? (
          <Button variant="secondary" onClick={limparFiltros}>
            Limpar filtros
          </Button>
        ) : podeCriar ? (
          <Button onClick={abrirCriacao}>
            <Plus aria-hidden="true" />
            Novo veículo
          </Button>
        ) : undefined
      }
    />
  )

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Frota</h1>
          <p className="text-sm text-fg-secondary">
            Os veículos com placa. O cliente aluga a categoria; o carro é escolhido na alocação.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <SeletorDeVisao visao={visao} aoMudar={setVisao} />

          {podeCriar ? (
            <Button onClick={abrirCriacao}>
              <Plus aria-hidden="true" />
              Novo veículo
            </Button>
          ) : null}
        </div>
      </div>

      {listasDeApoioComErro ? (
        <Alert variant="warning">
          <AlertDescription>
            Não foi possível carregar as categorias e as bases. Os dois filtros ficam vazios até
            elas voltarem — a lista de veículos abaixo continua correta.
          </AlertDescription>
          <Button
            variant="secondary"
            size="sm"
            className="mt-2"
            onClick={() => {
              void categorias.refetch()
              void locais.refetch()
            }}
          >
            Tentar de novo
          </Button>
        </Alert>
      ) : null}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <div className="space-y-1.5 lg:col-span-2">
          <Label htmlFor="filtro-busca-frota">Buscar</Label>
          <div className="relative">
            <Search
              aria-hidden="true"
              className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2
                text-fg-muted"
            />
            <Input
              id="filtro-busca-frota"
              type="search"
              value={buscaDigitada}
              onChange={(evento) => {
                setPagina(1)
                setBuscaDigitada(evento.target.value)
              }}
              placeholder="Placa, marca ou modelo"
              className="pl-9"
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="filtro-categoria">Categoria</Label>
          <Select
            id="filtro-categoria"
            value={categoryId}
            onChange={(evento) => {
              setPagina(1)
              setCategoryId(evento.target.value)
            }}
          >
            {/* Enquanto a lista não chega, o seletor com uma opção só é
                indistinguível de uma frota sem categoria nenhuma. */}
            <option value="">{categorias.isPending ? 'Carregando…' : 'Todas'}</option>
            {(categorias.data?.itens ?? []).map((categoria) => (
              <option key={categoria.id} value={categoria.id}>
                {categoria.nome}
              </option>
            ))}
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="filtro-base">Base</Label>
          <Select
            id="filtro-base"
            value={locationId}
            onChange={(evento) => {
              setPagina(1)
              setLocationId(evento.target.value)
            }}
          >
            <option value="">{locais.isPending ? 'Carregando…' : 'Todas'}</option>
            {(locais.data?.itens ?? []).map((local) => (
              <option key={local.id} value={local.id}>
                {local.nome}
              </option>
            ))}
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="filtro-status-veiculo">Situação</Label>
          <Select
            id="filtro-status-veiculo"
            value={status}
            onChange={(evento) => {
              setPagina(1)
              const valor = evento.target.value
              setStatus(STATUS_DE_VEICULO.find((item) => item === valor) ?? '')
            }}
          >
            <option value="">Todas</option>
            {STATUS_DE_VEICULO.map((item) => (
              <option key={item} value={item}>
                {ROTULO_DO_STATUS_DE_VEICULO[item].rotulo}
              </option>
            ))}
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="filtro-blindagem">Blindagem vencendo em</Label>
          <Select
            id="filtro-blindagem"
            value={blindagem === '' ? '' : String(blindagem)}
            onChange={(evento) => {
              setPagina(1)
              const valor = evento.target.value
              setBlindagem(JANELAS_DE_BLINDAGEM.find((dias) => String(dias) === valor) ?? '')
            }}
          >
            <option value="">Qualquer prazo</option>
            {JANELAS_DE_BLINDAGEM.map((dias) => (
              <option key={dias} value={dias}>
                {dias} dias
              </option>
            ))}
          </Select>
        </div>

        <div className="flex items-end">
          <label className="flex cursor-pointer items-start gap-2 py-2 text-sm text-fg-secondary">
            <Checkbox
              checked={incluirBaixados}
              onChange={(evento) => {
                setPagina(1)
                setIncluirBaixados(evento.target.checked)
              }}
              className="mt-0.5"
            />
            <span>Incluir veículos baixados</span>
          </label>
        </div>
      </div>

      {visao === 'tabela' ? (
        <TabelaDeDados
          colunas={colunas}
          dados={itens}
          carregando={consulta.isPending}
          erro={consulta.isError ? consulta.error : undefined}
          aoTentarNovamente={() => {
            void consulta.refetch()
          }}
          legenda="Veículos da frota"
          estadoVazio={estadoVazio}
        />
      ) : consulta.isPending ? (
        <LoadingState rotulo="Carregando a frota…" />
      ) : consulta.isError ? (
        <ErrorState
          titulo="Não foi possível carregar a frota"
          erro={consulta.error}
          aoTentarNovamente={() => {
            void consulta.refetch()
          }}
        />
      ) : itens.length === 0 ? (
        estadoVazio
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {itens.map((veiculo) => (
            <CartaoDeVeiculo
              key={veiculo.id}
              veiculo={veiculo}
              podeEditar={podeEditar}
              podeBaixar={podeBaixar}
              aoEditar={abrirEdicao}
              aoMudarStatus={setVeiculoDoStatus}
              aoBaixar={setVeiculoDaBaixa}
            />
          ))}
        </div>
      )}

      <Paginacao
        pagina={dados?.pagina ?? pagina}
        tamanho={dados?.tamanho ?? TAMANHO_DA_PAGINA}
        total={dados?.total ?? 0}
        aoMudarDePagina={setPagina}
        desabilitado={consulta.isFetching}
      />

      <DialogoDeVeiculo
        aberto={dialogoAberto}
        veiculo={emEdicao}
        aoFechar={() => {
          setDialogoAberto(false)
        }}
      />

      <DialogoDeStatus
        veiculo={veiculoDoStatus}
        aoFechar={() => {
          setVeiculoDoStatus(null)
        }}
      />

      <DialogoDeBaixa
        veiculo={veiculoDaBaixa}
        aoFechar={() => {
          setVeiculoDaBaixa(null)
        }}
      />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Peças da tela
// ---------------------------------------------------------------------------

interface SeletorDeVisaoProps {
  readonly visao: Visao
  readonly aoMudar: (visao: Visao) => void
}

/**
 * Alternador tabela/cards.
 *
 * `aria-pressed` e não `role="radio"`: são dois botões que ligam e desligam uma
 * forma de ver a mesma lista, não a escolha de um valor de formulário. E os dois
 * têm rótulo escrito — ícone sozinho aqui viraria adivinhação.
 */
function SeletorDeVisao({ visao, aoMudar }: SeletorDeVisaoProps): ReactElement {
  return (
    <div
      role="group"
      aria-label="Formato da lista"
      className="flex items-center gap-1 rounded-lg border border-border bg-surface p-1"
    >
      {VISOES.map((opcao) => {
        const selecionado = visao === opcao
        const Icone = opcao === 'tabela' ? Rows3 : LayoutGrid

        return (
          <Button
            key={opcao}
            size="sm"
            variant={selecionado ? 'secondary' : 'ghost'}
            aria-pressed={selecionado}
            onClick={() => {
              aoMudar(opcao)
            }}
            className={cn(!selecionado && 'border-transparent')}
          >
            <Icone aria-hidden="true" />
            {opcao === 'tabela' ? 'Tabela' : 'Cards'}
          </Button>
        )
      })}
    </div>
  )
}

/**
 * A validade do certificado com a gravidade calculada pelo mesmo corte do
 * servidor (`gravidadeDoVencimento`). Sem certificado é um estado próprio —
 * numa locadora de blindados, "não sei" não é o mesmo que "está em dia".
 */
function ValidadeDaBlindagem({ data }: { readonly data: string | null }): ReactElement {
  if (data === null) {
    return (
      <span className="text-xs text-fg-muted" title="Sem certificado de blindagem registrado">
        {SEM_VALOR}
      </span>
    )
  }

  const gravidade = gravidadeDaData(data)

  return (
    <div className="flex flex-col items-start gap-1">
      <span className="whitespace-nowrap tabular-money text-sm text-fg-secondary">
        {formatarData(data)}
      </span>
      {gravidade === null ? null : <BadgeDeGravidadeDeAlerta gravidade={gravidade} />}
    </div>
  )
}

interface AcoesProps {
  readonly veiculo: Veiculo
  readonly podeEditar: boolean
  readonly podeBaixar: boolean
  readonly aoEditar: (veiculo: Veiculo) => void
  readonly aoMudarStatus: (veiculo: Veiculo) => void
  readonly aoBaixar: (veiculo: Veiculo) => void
}

/**
 * O menu da linha.
 *
 * Veículo baixado só oferece leitura. A API recusa editar e mudar status de um
 * carro fora da frota (`VEICULO_BAIXADO`, 409) enquanto a baixa não for
 * revertida — e um item de menu que só termina em erro é pior que item
 * nenhum: ele promete uma ação que não existe.
 */
function AcoesDoVeiculo({
  veiculo,
  podeEditar,
  podeBaixar,
  aoEditar,
  aoMudarStatus,
  aoBaixar,
}: AcoesProps): ReactElement {
  const naFrota = veiculo.dataDeBaixa === null

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label={`Ações de ${identificacaoDoVeiculo(veiculo)}`}
          className="size-8"
        >
          <EllipsisVertical aria-hidden="true" />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuItem asChild>
          <Link to="/frota/$veiculoId" params={{ veiculoId: veiculo.id }}>
            <ExternalLink aria-hidden="true" />
            Abrir detalhe
          </Link>
        </DropdownMenuItem>

        {podeEditar && naFrota ? (
          <>
            <DropdownMenuItem
              onSelect={() => {
                aoEditar(veiculo)
              }}
            >
              <Pencil aria-hidden="true" />
              Editar cadastro
            </DropdownMenuItem>

            <DropdownMenuItem
              onSelect={() => {
                aoMudarStatus(veiculo)
              }}
            >
              <ArrowRightLeft aria-hidden="true" />
              Mudar status
            </DropdownMenuItem>
          </>
        ) : null}

        {podeBaixar && naFrota ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={() => {
                aoBaixar(veiculo)
              }}
            >
              <CircleArrowDown aria-hidden="true" />
              Baixar da frota
            </DropdownMenuItem>
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

type CartaoProps = AcoesProps

/**
 * O card é a leitura do balcão: identificação, situação e o que impede o carro
 * de sair, na ordem em que a pergunta é feita.
 */
function CartaoDeVeiculo({ veiculo, ...acoes }: CartaoProps): ReactElement {
  return (
    <Card className="flex flex-col">
      <CardHeader className="flex flex-row items-start justify-between gap-2 pb-3">
        <div className="min-w-0">
          <Link
            to="/frota/$veiculoId"
            params={{ veiculoId: veiculo.id }}
            className="block truncate text-base font-semibold text-foreground hover:underline"
          >
            {veiculo.modelo}
          </Link>
          <p className="truncate text-xs tabular-money text-fg-secondary">
            {formatarPlaca(veiculo.placa)} · {veiculo.marca} · {veiculo.anoFabricacao}/
            {veiculo.anoModelo}
          </p>
        </div>

        <AcoesDoVeiculo veiculo={veiculo} {...acoes} />
      </CardHeader>

      <CardContent className="flex flex-1 flex-col gap-3">
        <div className="flex flex-wrap items-center gap-1.5">
          <BadgeDeStatusDoVeiculo status={veiculo.statusAtual} />
          <BadgeDeNivelDeBlindagem nivel={veiculo.categoria.nivelDeBlindagem} />
          {veiculo.dataDeBaixa === null ? null : <Badge variant="neutral">Baixado</Badge>}
        </div>

        <dl className="grid gap-2 text-sm">
          <div className="flex items-baseline justify-between gap-2">
            <dt className="text-xs text-fg-muted">Categoria</dt>
            <dd className="min-w-0 truncate text-right text-foreground">
              {veiculo.categoria.nome}
            </dd>
          </div>

          <div className="flex items-baseline justify-between gap-2">
            <dt className="text-xs text-fg-muted">Base</dt>
            <dd className="min-w-0 truncate text-right text-fg-secondary">
              {veiculo.local === null ? SEM_VALOR : veiculo.local.nome}
            </dd>
          </div>

          <div className="flex items-baseline justify-between gap-2">
            <dt className="text-xs text-fg-muted">Hodômetro</dt>
            <dd className="money-cell text-fg-secondary">{formatarKm(veiculo.hodometroAtual)}</dd>
          </div>

          <div className="flex items-baseline justify-between gap-2">
            <dt className="text-xs text-fg-muted">Blindagem até</dt>
            <dd className="text-right">
              <ValidadeDaBlindagem data={veiculo.certificadoDeBlindagemValidade} />
            </dd>
          </div>
        </dl>
      </CardContent>
    </Card>
  )
}

interface DialogoDeBaixaProps {
  readonly veiculo: Veiculo | null
  readonly aoFechar: () => void
}

/**
 * Confirmação da baixa.
 *
 * Baixar não apaga — a API grava `dataDeBaixa`, move o status para indisponível
 * e escreve a linha da trilha; custo e histórico continuam apontando para o
 * carro. Ainda assim é a única ação da tela que tira um ativo de circulação, e
 * ela não pode acontecer por um clique errado num item de menu.
 *
 * O texto NÃO promete que a baixa se desfaz por aqui: reverter é um `PATCH` com
 * `dataDeBaixa: null` que nenhuma tela desta fase envia, e prometer o desfazer
 * num aviso de confirmação é justamente o que faria alguém clicar sem ler.
 */
function DialogoDeBaixa({ veiculo, aoFechar }: DialogoDeBaixaProps): ReactElement {
  const baixar = useBaixarVeiculo()

  function confirmar(): void {
    if (veiculo === null) {
      return
    }

    baixar.mutate(veiculo.id, {
      onSuccess: (baixado) => {
        toast.success('Veículo baixado', {
          description: `${identificacaoDoVeiculo(baixado)} saiu da frota. O histórico e os custos continuam disponíveis.`,
        })
        aoFechar()
      },
      onError: (causa: unknown) => {
        toast.error('Não foi possível baixar o veículo', { description: errorMessage(causa) })
      },
    })
  }

  return (
    <Dialog
      open={veiculo !== null}
      onOpenChange={(proximo) => {
        if (!proximo) {
          aoFechar()
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Baixar da frota</DialogTitle>
          <DialogDescription>
            {veiculo === null
              ? 'Selecione um veículo.'
              : `${identificacaoDoVeiculo(veiculo)} deixa de estar disponível para alocação.`}
          </DialogDescription>
        </DialogHeader>

        <Alert variant="warning">
          <AlertDescription>
            O cadastro não é apagado: a data de baixa é registrada, o status vai para indisponível e
            a trilha guarda quem fez. Custos e histórico continuam consultáveis, mas o veículo passa
            a ser só leitura no painel — editar e mudar status deixam de estar disponíveis.
          </AlertDescription>
        </Alert>

        <DialogFooter>
          <Button type="button" variant="secondary" onClick={aoFechar} disabled={baixar.isPending}>
            Cancelar
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={confirmar}
            disabled={baixar.isPending}
          >
            {baixar.isPending ? <LoaderCircle className="animate-spin" aria-hidden="true" /> : null}
            Baixar veículo
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
